import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const KIND_COLORS = {
  budget_alert: 'text-amber-300',
  system: 'text-slate-300',
  info: 'text-sky-300',
  recurring: 'text-emerald-300',
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`);
      setUnread(r.data.count || 0);
    } catch (err) {
      // Background poll — only surface when the error is meaningful.
      // 401s during logout are expected; transient 5xxs we just log to console.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn('[NotificationBell] unread-count failed:', err.response.status);
      }
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 30 } });
      setItems(r.data || []);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn('[NotificationBell] list failed:', err.response.status);
      }
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      fetchUnread();
    } catch (err) {
      console.warn('[NotificationBell] markRead failed:', err?.response?.status);
      toast.error('Could not mark as read');
    }
  };

  const markAll = async () => {
    try {
      await axios.patch(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success('All marked as read');
    } catch (err) {
      console.warn('[NotificationBell] markAll failed:', err?.response?.status);
      toast.error('Could not mark all read');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        className="relative flex items-center justify-center w-10 h-10 rounded-sm hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-ibm font-bold rounded-full flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 max-h-[480px] bg-white border border-slate-200 rounded-sm shadow-xl z-50 overflow-hidden text-slate-900"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <span className="font-chivo font-bold">Notifications</span>
              {items.some((n) => !n.read) && (
                <button
                  onClick={markAll}
                  data-testid="notification-mark-all-read"
                  className="text-xs font-ibm text-slate-600 hover:text-slate-900 flex items-center gap-1"
                >
                  <CheckCheck size={14} /> Mark all read
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[420px]">
              {items.length === 0 ? (
                <div className="p-8 text-center text-sm font-ibm text-slate-500" data-testid="notification-empty">
                  No notifications yet
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                      !n.read ? 'bg-slate-50/60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`mt-1 inline-block w-1.5 h-1.5 rounded-full ${!n.read ? 'bg-emerald-500' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-ibm font-semibold text-sm text-slate-900 truncate">{n.title}</span>
                          <span className={`text-[10px] uppercase tracking-widest font-ibm ${KIND_COLORS[n.kind] || 'text-slate-400'} text-slate-500`}>
                            {n.kind}
                          </span>
                        </div>
                        <p className="text-xs font-ibm text-slate-600 mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] font-ibm text-slate-400 mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
